import { useState } from "react";
import { pendingCases } from "@/mocks/melonData";
import { SectionHeading, SectionShell } from "./brand";

type Counts = Record<string, { clue: number; join: number }>;
type Msg = Record<string, string>;

export default function PendingCases() {
  const [counts, setCounts] = useState<Counts>(() =>
    Object.fromEntries(
      pendingCases.map((p) => [p.id, { clue: p.clue, join: 0 }]),
    ),
  );
  const [msg, setMsg] = useState<Msg>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});

  function react(id: string, type: "clue" | "join") {
    if (busy[id]) return;
    setBusy((b) => ({ ...b, [id]: true }));
    setCounts((c) => ({
      ...c,
      [id]: {
        ...c[id],
        [type]: c[id][type] + (type === "clue" ? 1 : 1),
      },
    }));
    setMsg((m) => ({
      ...m,
      [id]: type === "clue" ? "线索已记录，感谢你的补充" : "已加入共同求证，可关注进展",
    }));
    window.setTimeout(() => {
      setBusy((b) => ({ ...b, [id]: false }));
    }, 600);
  }

  return (
    <SectionShell id="pending" className="bg-accent-100/40">
      <SectionHeading
        eyebrow="求证进行中 · 别让假料带节奏"
        title="这些瓜正在核验，等一条关键线索"
        desc="还没有定论的爆料都挂在这里。你可以补充线索，也可以一键加入共同求证——每一条有效补料都可能成为定级的关键一击。"
      />

      <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2">
        {pendingCases.map((p) => {
          const cur = counts[p.id];
          return (
            <div
              key={p.id}
              className="flex flex-col rounded-2xl border border-background-200 bg-background-50 p-6"
            >
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 whitespace-nowrap">
                  <i className="ri-time-line" />
                  求证中
                </span>
                <span className="text-xs text-foreground-600">{p.tag}</span>
              </div>

              <div className="mt-4 flex items-start gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-700 text-lg">
                  <i className={p.icon} />
                </span>
                <h3 className="font-semibold text-foreground-950 leading-snug text-base">
                  {p.title}
                </h3>
              </div>

              <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                {p.brief}
              </p>

              <div className="mt-4 flex items-center gap-5 rounded-xl bg-background-100 px-4 py-3 text-sm">
                <span className="inline-flex items-center gap-1.5 text-foreground-800">
                  <i className="ri-question-answer-line text-primary-600" />
                  已收线索{" "}
                  <b className="text-primary-700 font-semibold">{cur.clue}</b> 条
                </span>
                <span className="inline-flex items-center gap-1.5 text-foreground-800">
                  <i className="ri-team-line text-accent-600" />
                  共同求证{" "}
                  <b className="text-accent-700 font-semibold">{cur.join}</b> 次
                </span>
              </div>

              <div className="mt-5 flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={() => react(p.id, "clue")}
                  className="inline-flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-4 py-2.5 text-sm text-background-50 hover:bg-primary-600 disabled:opacity-60"
                >
                  <i className="ri-add-line" />
                  我补充线索
                </button>
                <button
                  type="button"
                  onClick={() => react(p.id, "join")}
                  className="inline-flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-full border border-accent-300 bg-background-50 px-4 py-2.5 text-sm text-accent-800 hover:bg-accent-100 disabled:opacity-60"
                >
                  <i className="ri-flag-2-line" />
                  共同求证
                </button>
              </div>

              <p
                className={`mt-2 min-h-[18px] text-xs text-accent-700 transition-opacity ${
                  msg[p.id] ? "opacity-100" : "opacity-0"
                }`}
              >
                {msg[p.id]}
              </p>
            </div>
          );
        })}
      </div>

      <p className="mt-6 text-center text-xs text-foreground-600">
        每条补料都会经过编号建档与真实性初筛，不会被匿名引用直接当作结论。
      </p>
    </SectionShell>
  );
}